import { useState, useCallback, useRef } from "react";
import {
  getExtTalLocationsApi,
  getGigTitle,
  getSkillSuggestionsApi,
  fetchSearchLocationAPI,
  searchCompaniesAndIndustriesApi,
  searchRegionSuggestionsApi,
  searchStateSuggestionsApi,
} from "../../../../../API/API";

export const useLocationSearch = () => {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const labelMap = useRef({});
  const timer = useRef(null);

  const onSearch = useCallback((value) => {
    if (timer.current) clearTimeout(timer.current);
    if (!value) { return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await getExtTalLocationsApi(value);
        const raw = res?.data?.data ?? res?.data ?? [];
        const mapped = Array.isArray(raw)
          ? raw.filter(Boolean).map((item) => {
              const label = item?.label || String(item);
              const sub_label = item?.sub_label || '';
              const type = item?.value?.type;
              const val =
                type === "city" || type === "location"
                  ? sub_label || item?.value?.CityName || label
                  : type === "state" || type === "region"
                  ? sub_label || item?.value?.RegionName || label
                  : type === "country"
                  ? sub_label || item?.value?.CountryName || label
                  : sub_label || label;
              labelMap.current[val] = label;
              return { label, sub_label, value: val };
            })
          : [];
        setOpts(mapped);
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 500);
  }, []);

  return { opts, loading, onSearch, labelMap: labelMap.current };
};

const toActionPayload = (value) => ({
  payload: value
    ? {
        obj: {
          value,
        },
      }
    : undefined,
});

const usePmtLocationFacetSearch = ({ api, getRawList, mapItem }) => {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const onSearch = useCallback((value) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api(toActionPayload(value));
        const raw = getRawList?.(res?.data) || [];
        const mapped = Array.isArray(raw)
          ? raw
              .map((item) => {
                try {
                  return mapItem?.(item);
                } catch {
                  return null;
                }
              })
              .filter(Boolean)
          : [];

        setOpts(mapped);
      } catch (error) {
        console.warn("Failed to load PMT location filter suggestions", error);
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, [api, getRawList, mapItem]);

  return { opts, loading, onSearch };
};

export const usePmtCountrySearch = () =>
  usePmtLocationFacetSearch({
    api: searchStateSuggestionsApi,
    getRawList: (data) => data?.location || data || [],
    mapItem: (item) => {
      const id = item?.CountryId ?? item?.id;
      const label = item?.CountryName ?? item?.name;

      return id && label ? { label, value: String(id) } : null;
    },
  });

export const usePmtRegionSearch = () =>
  usePmtLocationFacetSearch({
    api: searchRegionSuggestionsApi,
    getRawList: (data) => data?.location || data || [],
    mapItem: (item) => {
      const id = item?.RegionId ?? item?.id;
      const label = item?.RegionName ?? item?.name;

      return id && label ? { label, value: String(id) } : null;
    },
  });

export const usePmtCitySearch = () =>
  usePmtLocationFacetSearch({
    api: (params) => fetchSearchLocationAPI({
      payload: params?.payload?.obj?.value || "",
    }),
    getRawList: (data) => data || [],
    mapItem: (item) => {
      const id = item?.CityId ?? item?.id;
      const label = item?.Location ?? item?.CityName ?? item?.name;

      return id && label ? { label, value: String(id) } : null;
    },
  });

const getDefaultCompanyFilterBody = (search = "") => ({
  obj: {
    _page: 1,
    _perPage: "24",
    _sort: "MOST_RELEVANT",
    search,
    hide: true,
    NearBy: null,
    Latitude: null,
    Longitude: null,
    maxDistance: "50",
    BudgetMax: null,
    BudgetMin: null,
    BudgetType: null,
    GigType: null,
    CityId: [],
    RegionId: [],
    CountryId: [],
    SkillsId: [],
    Experience: [],
    searchType: "Skills",
    ProfessionCode: null,
  },
});

export const usePmtCompanySearch = () => {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const onSearch = useCallback((value) => {
    if (timer.current) clearTimeout(timer.current);
    if (!value) {
      setOpts([]);
      return;
    }

    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchCompaniesAndIndustriesApi({
          payload: {
            value: `company=${encodeURIComponent(value)}`,
            body: getDefaultCompanyFilterBody(value),
          },
        });
        const companies = res?.data?.result?.orgDetails?.companies || [];
        const mapped = Array.isArray(companies)
          ? companies
              .map((item) => {
                const companyName =
                  item?._id?.trim?.() || item?.CompanyName || item?.name;

                return companyName
                  ? {
                      label: companyName,
                      value: companyName,
                    }
                  : null;
              })
              .filter(Boolean)
          : [];

        setOpts(mapped);
      } catch (error) {
        console.warn("Failed to load PMT company filter suggestions", error);
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  return { opts, loading, onSearch };
};

export const useTitleSearch = () => {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const onSearch = useCallback((value) => {
    if (timer.current) clearTimeout(timer.current);
    if (!value) { setOpts([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await getGigTitle({ title: value, professionCode: "" });
        const raw = res?.data?.data ?? res?.data ?? [];
        setOpts(
          Array.isArray(raw)
            ? raw
                .filter(Boolean)
                .map((item) => {
                  const label = item?.Title ?? item?.title ?? String(item);
                  return { label, value: label };
                })
            : []
        );
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  return { opts, loading, onSearch };
};

export const useSkillSearch = () => {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const onSearch = useCallback((value) => {
    if (timer.current) clearTimeout(timer.current);
    if (!value) { setOpts([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await getSkillSuggestionsApi(value);
        const raw = res?.data?.data ?? res?.data ?? [];
        setOpts(
          Array.isArray(raw)
            ? raw
                .filter(Boolean)
                .map((item) => {
                  const label = item?.SkillName ?? item?.skillName ?? String(item);
                  return { label, value: label };
                })
            : []
        );
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  return { opts, loading, onSearch };
};
